import { Typography } from '@fox/atoms';
import { useComponentClassContext } from '../component-class-context';
import { HoverWrapper, useComponentStyleContext } from '../component-style-context';
import { PAGE_TYPES } from '../page-types';
import { HeaderLinkProps, MenuItem } from './contracts';

function createChunks(menuItems: MenuItem[], maxRowsPerColumn: number) {
	return menuItems?.reduce((prev, cur, index) => {
		const chunkIndex = Math.floor(index / maxRowsPerColumn);

		if (!prev[chunkIndex]) {
			prev[chunkIndex] = [];
		}

		prev[chunkIndex].push(cur);

		return prev;
	}, []);
}

function RenderChildren({
	menuItems,
	depth,
	maxRowsPerColumn,
	currentUrl,
	isMegaMenu
}: {
	menuItems: MenuItem[];
	maxRowsPerColumn: number;
	currentUrl: string;
	depth?: number;
	isMegaMenu?: boolean;
}) {
	const {
		font,
		fontColour,
		fontSizeRem,
		fontTextShadow,
		subtextFont,
		subtextFontColour,
		headerTextShadow,
		textAlignment,
		linkHoverColour,
		linkHoverUnderline
	} = useComponentStyleContext();
	const subHeaderStyle = { ...subtextFont, ...subtextFontColour, ...headerTextShadow };
	const fontStyle = { ...font, ...fontColour, ...fontSizeRem, ...fontTextShadow };

	const { headerSubtextClass } = useComponentClassContext();

	const currentDepth = depth ?? 0;
	const chunks =
		maxRowsPerColumn !== undefined && maxRowsPerColumn !== null && currentDepth > 0
			? createChunks(menuItems, maxRowsPerColumn)
			: [menuItems];
	return (
		<>
			{chunks.map((chunk, i) => {
				return (
					<div
						key={i}
						style={
							currentDepth > 0
								? { display: 'flex', flexDirection: 'column' }
								: isMegaMenu
								? { padding: '2rem', gap: '3rem', display: 'flex', flexWrap: 'wrap' }
								: { padding: '1rem', display: 'flex', flexDirection: 'column' }
						}
					>
						{chunk.map((menuItem: MenuItem) => {
							return (
								<div key={menuItem.id} style={{ ...textAlignment, display: 'flex', flexDirection: 'column' }}>
									{menuItem.items?.length > 0 ? (
										<>
											{menuItem.type === PAGE_TYPES.PAGE_FOLDER ? ( //what do we do with news pages etc?
												<Typography
													style={{
														whiteSpace: 'nowrap',
														marginBottom: isMegaMenu ? '1rem' : '0.5rem',
														cursor: 'default',
														...subHeaderStyle,
														...(menuItem.url === currentUrl
															? { ...linkHoverColour, ...linkHoverUnderline }
															: {})
													}}
													className={headerSubtextClass}
												>
													{menuItem.name}
												</Typography>
											) : (
												<a
													style={{
														textDecoration: 'none',
														color: 'inherit',
														width: 'max-content',
														display: 'block'
													}}
													href={menuItem.url}
													target={menuItem.openInNewTab ? '_blank' : '_self'}
												>
													<Typography
														style={{
															whiteSpace: 'nowrap',
															marginBottom: isMegaMenu ? '1rem' : '0.5rem',

															...subHeaderStyle,
															...(menuItem.url === currentUrl
																? { ...linkHoverColour, ...linkHoverUnderline }
																: {})
														}}
														className={`link-style ${headerSubtextClass}`}
													>
														{menuItem.name}
													</Typography>
												</a>
											)}
											{menuItem.items?.length &&
											menuItem.type !== PAGE_TYPES.NEWS_LIST &&
											menuItem.type !== PAGE_TYPES.EVENT_LIST ? (
												<div
													style={{
														display: 'grid',
														columnGap: '3rem',
														paddingLeft: '1rem',
														gridTemplateColumns: `repeat(${
															createChunks(menuItem.items, maxRowsPerColumn)?.length
														}, 1fr)`
													}}
												>
													<RenderChildren
														menuItems={menuItem.items}
														depth={currentDepth + 1}
														maxRowsPerColumn={maxRowsPerColumn}
														currentUrl={currentUrl}
													/>
												</div>
											) : null}
										</>
									) : (
										<a
											style={{
												textDecoration: 'none',
												color: 'inherit',
												width: 'max-content',
												display: 'block'
											}}
											href={menuItem.url}
											target={menuItem.openInNewTab ? '_blank' : '_self'}
										>
											<Typography
												className={`link-style ${currentDepth === 0 ? headerSubtextClass : ''}`}
												style={{
													...(currentDepth === 0 ? subHeaderStyle : fontStyle),
													whiteSpace: 'nowrap',
													marginBottom: '0.5rem',
													...(menuItem.url === currentUrl ? { ...linkHoverColour, ...linkHoverUnderline } : {})
												}}
											>
												{menuItem.name}
											</Typography>
										</a>
									)}
								</div>
							);
						})}
					</div>
				);
			})}
		</>
	);
}

export function HeaderLinkOverlay({ menuItem, maxRowsPerColumn, currentUrl, isMegaMenu }: HeaderLinkProps) {
	const { borderColour, borderRadius, borderWidth, boxShadowStyle, borderStyle, backgroundColour, linkHoverColour, linkHoverUnderline } =
		useComponentStyleContext();
	return menuItem.items?.length ? (
		<div style={{ ...borderColour, ...borderRadius, ...borderWidth, ...boxShadowStyle, ...borderStyle, ...backgroundColour }}>
			<HoverWrapper $linkHoverColour={linkHoverColour?.color} $linkHoverUnderline={linkHoverUnderline?.textDecoration === 'underline'}>
				<RenderChildren
					menuItems={menuItem.items}
					maxRowsPerColumn={maxRowsPerColumn}
					currentUrl={currentUrl}
					isMegaMenu={isMegaMenu}
				/>
			</HoverWrapper>
		</div>
	) : (
		<></>
	);
}
